/**
 * Textes du nombre de sources dans le tunnel de souscription (DEV-621, écran M11).
 *
 * INTENTION. Le tunnel aide à choisir un plafond, il n'explique pas la règle
 * de facturation (décision du 13/10 : tunnel sans règle). Il dit le plafond
 * choisi (« N sources par période ») et, quand le compte a déjà des sources,
 * combien sont facturées aujourd'hui, pour situer le choix. Ce nombre est lu
 * dans le plafond servi (`authStore.companyPlafond`) ; sans plafond servi, ou
 * sans source facturée, la phrase est omise : une absence n'est jamais un 0.
 *
 * Règles de rédaction : « sources » (jamais « compteurs »), « facturées »
 * (jamais « possédez »), aucun détail technique.
 */
import type { CompanyPlafond } from "@core/types/auth"

/** Plafond le plus haut proposé en ligne (`MAX_OFFER_SOURCES`, api-metier `stripe.service.js`) */
export const PLAFOND_SOUSCRIPTION_EN_LIGNE = 250

/** « L'abonnement de base inclut 30 sources, extensible jusqu'à 250 sources. » */
export const TEXTE_BASE_SOUSCRIPTION = `L'abonnement de base inclut 30 sources, extensible jusqu'à ${PLAFOND_SOUSCRIPTION_EN_LIGNE} sources.`

/** Phrase d'appel au-delà du plafond en ligne (suivie de l'adresse de contact) */
export const TEXTE_AU_DELA_SOUSCRIPTION = `Au-delà de ${PLAFOND_SOUSCRIPTION_EN_LIGNE} sources, contactez-nous à l'adresse suivante :`

/**
 * Plafond choisi : « 30 sources par période ». La période est celle de
 * l'abonnement (mensuelle), d'où « par période » et jamais « par mois ».
 * @param n - Nombre de sources du palier choisi
 */
export function textePlafondChoisi(n: number): string {
    return n > 1 ? `${n} sources par période` : `${n} source par période`
}

/**
 * « Vous avez aujourd'hui 42 sources facturées. » à partir du plafond servi ;
 * `null` sans plafond servi ou sans source facturée (phrase omise, jamais 0).
 * @param plafond - Plafond servi du compte connecté, s'il y en a un
 */
export function texteFactureesAujourdhui(
    plafond: Pick<CompanyPlafond, "facturees"> | null | undefined
): string | null {
    const n = plafond?.facturees
    if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return null
    return n > 1
        ? `Vous avez aujourd'hui ${n} sources facturées.`
        : "Vous avez aujourd'hui 1 source facturée."
}
